import { ref, computed, watch } from 'vue'
import { isInStock } from '@scayle/storefront-nuxt'
import type {
  BasketItemDisplayDataItem,
  Product,
  Variant,
} from '@scayle/storefront-nuxt'
import { useDeviceType } from '~/composables/useDeviceType'
import useAgsWishlist from '~/composables/useAgsWishlist'
import useAgsBasket from '~/composables/useAgsBasket'
import useUiState from '~/composables/ui/useUiState'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useSizeDisplayData from '../composables/useSizeDisplayData'
import { useBasketDeliveryEstimate } from '../composables/useBasketDeliveryEstimate'
import { getDeliveryEstimation } from '../utils/date'
import { getIsQuantityChangeDisabled } from '~/utils/product'
import { getDeliveryEstimationInDays } from '../utils/shopConfiguration'
import type useSmartShopConfiguration from '../composables/useSmartShopConfiguration'

export interface UseBasketHandlingProps {
  activeVariant: Variant | undefined
  pricePromotionKey: string | undefined
  product: Product
  productOrderNumber: string | undefined
  productVariants: Variant[] | undefined
  shopConfiguration: ReturnType<
    typeof useSmartShopConfiguration
  >['data']['value']
  trackingType: 'pdp' | 'advantage_page'
}

export function useBasketHandling(props: UseBasketHandlingProps) {
  const { isMobile } = useDeviceType()
  const { openBackInStockModal, openAddToBasketFlyout } = useUiState()
  const { trackAddToBasket, trackAddToWishlist, trackRemoveFromWishlist } =
    useTrackingEvents()
  const { getSizeDisplayData } = useSizeDisplayData()
  const { getBaskedDeliveryEstimationWithDate } = useBasketDeliveryEstimate()

  // Initialize basket and wishlist
  const basket = useAgsBasket(props.pricePromotionKey)
  const wishlist = useAgsWishlist()

  // State management
  const showSizeError = ref(false)
  const errorTimeout = ref<NodeJS.Timeout | null>(null)
  const isCtaHovered = ref(false)

  // Computed properties
  const deliveryEstimationInDays = computed(() =>
    getDeliveryEstimationInDays(props.shopConfiguration),
  )

  const isInBasket = computed(() => {
    // Reactive basket item checker that properly tracks basket items
    // Just checking the findItem is not enough, because it doesn't track the basket items
    return basket?.contains(
      { productId: props.product?.id },
      basket.items.value,
    )
  })

  const isInWishlist = computed(
    () => !!wishlist.findItem({ productId: props.product?.id }),
  )

  const showBackInStockButton = computed(() => {
    return props.activeVariant && !isInStock(props.activeVariant)
  })

  const showAlreadyInCartButton = computed(() => {
    const items = basket?.items?.value ?? []
    return Boolean(
      props.activeVariant &&
      basket?.findItem(
        { variantId: props.activeVariant.id },
        basket.items.value,
      ),
    )
  })

  const canPurchase = computed(() => {
    if (!(props.product && props.productVariants)) {
      return false
    }
    if (!props.product.isSoldOut) {
      return true
    }
    for (const item of props.productVariants) {
      if (!item.stock.isSellableWithoutStock) {
        return false
      }
    }
    return true
  })

  const canPurchaseMultiple = computed(() => {
    if (!props.product) {
      return false
    }
    if (!getIsQuantityChangeDisabled(props.product ?? undefined)) {
      return true
    }
    return !isInBasket.value
  })

  // Error handling
  const showTimedNoVariantError = () => {
    showSizeError.value = true

    // Reset error after 3 seconds
    if (errorTimeout.value) clearTimeout(errorTimeout.value)
    errorTimeout.value = setTimeout(() => {
      showSizeError.value = false
      errorTimeout.value = null
    }, 3000)

    // Scroll behavior for mobile
    if (isMobile.value) {
      const button = document.getElementById('add-to-basket')
      if (button) {
        const rect = button.getBoundingClientRect()
        const isInLowerHalf = rect.top > window.innerHeight / 2

        if (!isInLowerHalf) {
          document.getElementById('product-configuration')?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          })
        }
      }
    }
  }

  // Basket operations
  const addItemToBasket = async () => {
    if (!props.activeVariant) {
      return
    }

    try {
      await basket.addItem({
        variantId: props.activeVariant.id,
        quantity: 1,
        displayData: {
          'attribute-2': getSizeDisplayData(
            props.activeVariant,
          ) as unknown as BasketItemDisplayDataItem,
          ...getBaskedDeliveryEstimationWithDate(
            getDeliveryEstimation(
              props.activeVariant,
              deliveryEstimationInDays.value,
            ),
          ),
        },
        customData: {
          ordernumber: props.productOrderNumber,
          deliveryEstimationDate: getDeliveryEstimation(
            props.activeVariant,
            deliveryEstimationInDays.value,
          ),
        },
      })

      const newBasket = useAgsBasket()
      await newBasket.refresh()

      openAddToBasketFlyout(props.activeVariant.id)

      if (props.product) {
        trackAddToBasket({
          product: props.product,
          variant: props.activeVariant,
          type: props.trackingType,
        })
      }
    } catch (error) {
      // A rejected basket add (stock/validation/network) previously surfaced
      // as an unhandled rejection; log and stop so the success flyout and
      // tracking don't fire on failure (ECOM-2690).
      console.error('Add to basket failed', error)
    }
  }

  // Wishlist operations
  const handleToggleWishlist = () => {
    // TODO: add return value event: 'added' or 'removed'
    if (!props.activeVariant && !props.product) {
      return
    }
    wishlist.toggleItem(
      props.activeVariant
        ? { variantId: props.activeVariant.id }
        : { productId: props.product.id },
    )

    const wasInWishlist = isInWishlist.value

    if (props.product) {
      if (wasInWishlist) {
        trackRemoveFromWishlist({
          product: props.product,
          variant: props.activeVariant,
        })
      } else {
        trackAddToWishlist({
          product: props.product,
          variant: props.activeVariant,
          type: props.trackingType,
        })
      }
    }
  }

  // Event handlers
  const handleAddToBasketClick = () => {
    if (!props.activeVariant) {
      showTimedNoVariantError()
      return
    }

    if (!isInStock(props.activeVariant)) {
      openBackInStockModal()
      return
    }

    addItemToBasket()
  }

  const handleMouseEnter = () => {
    isCtaHovered.value = true
    if (!props.activeVariant) {
      showSizeError.value = true
    }
  }

  const handleMouseLeave = () => {
    isCtaHovered.value = false
    // Only hide error if it wasn't triggered by click
    // If error was shown by click, let the timeout handle it
    if (!errorTimeout.value) {
      showSizeError.value = false
    }
  }

  const basketFabClicked = () => {
    if (props.activeVariant) {
      return addItemToBasket()
    }
    document.getElementById('product-configuration')?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }

  // Watch for activeVariant changes to clear errors
  watch(
    () => props.activeVariant,
    (variant) => {
      if (variant) {
        showSizeError.value = false
        if (errorTimeout.value) {
          clearTimeout(errorTimeout.value)
          errorTimeout.value = null
        }
      }
    },
  )

  // Watch for pricePromotionKey changes to update basket
  watch(
    () => props.pricePromotionKey,
    (ppk) => {
      if (ppk) basket.setPricePromotionKey(ppk)
    },
    { immediate: true },
  )

  return {
    // State
    showSizeError: readonly(showSizeError),
    isCtaHovered: readonly(isCtaHovered),

    // Computed
    isInBasket,
    isInWishlist,
    showBackInStockButton,
    showAlreadyInCartButton,
    deliveryEstimationInDays,
    canPurchase,
    canPurchaseMultiple,

    // Methods
    addItemToBasket,
    handleToggleWishlist,
    handleAddToBasketClick,
    handleMouseEnter,
    handleMouseLeave,
    basketFabClicked,

    // Basket instance
    basket,
  }
}
